import React, { useEffect, useReducer, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { TerraDraw } from 'terra-draw';

import reducer, { useTerraDrawEvents, useTerraDrawSnapshots } from './undoRedo';
import { Action, DrawingActionKind, State, isTerraDrawFeature } from './types';
import { useLoginContext } from '../../../../../contexts/LoginContext';
interface Props {
  draw: TerraDraw | null;
  onDrawingModeChange?: (mode: 'select' | 'rectangle') => void;
}

export const UndoRedoControl = ({ draw, onDrawingModeChange }: Props) => {
  const map = useMap();
  const { user, selectedProfileIndex, setError, updateRegions, initalized } =
    useLoginContext();
  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];

  // Create stable references to prevent infinite loops
  const updateRegionsRef = useRef(updateRegions);
  updateRegionsRef.current = updateRegions;

  const selectedProfileRef = useRef(selectedProfile);
  selectedProfileRef.current = selectedProfile;

  const userRef = useRef(user);
  userRef.current = user;

  const setErrorRef = useRef(setError);
  setErrorRef.current = setError;

  // Keep track of the last selected profile index to detect changes
  const lastSelectedProfileIndex = useRef<number | undefined>(undefined);
  const isInitialized = useRef<boolean>(false);
  const allowEmptyUpdateRef = useRef<boolean>(false);

  const [state, dispatch] = useReducer(
    (state: State, action: Action): State => {
      // Don't process actions until map is initialized and user is loaded
      if (
        !map ||
        !initalized ||
        !userRef.current ||
        selectedProfileIndex === undefined
      ) {
        return state;
      }
      return reducer(state, action, () => {
        // Empty function - updateRegions is handled in useEffect
      }) as State;
    },
    {
      past: [],
      now: [],
      future: [],
    }
  );

  // Initialize or reinitialize regions when map loads or profile changes
  useEffect(() => {
    if (
      !map ||
      !draw ||
      !initalized ||
      !userRef.current ||
      selectedProfileIndex === undefined
    ) {
      return;
    }

    // Check if this is the first initialization or if the profile has changed
    const isFirstLoad = !isInitialized.current;
    const profileChanged =
      lastSelectedProfileIndex.current !== selectedProfileIndex;

    // Only initialize on first load or profile change
    if (isFirstLoad || profileChanged) {
      // Clear existing user-drawn features if profile changed
      if (profileChanged && !isFirstLoad) {
        dispatch({ type: DrawingActionKind.CLEAR });
        draw.clear();
      }

      // Update tracking variables
      lastSelectedProfileIndex.current = selectedProfileIndex;
      isInitialized.current = true;

      // useTerraDrawSnapshots hook now handles loading saved regions into TerraDraw
      // We just need to set initial validation state
      const initialRegions = selectedProfileRef.current?.extRegions;
      const regionCount = initialRegions?.length ?? 0;
      if (regionCount > 0 && regionCount < 16) {
        setErrorRef.current(false);
      } else {
        setErrorRef.current(true);
      }
    }
  }, [map, draw, selectedProfileIndex, initalized]);

  useEffect(() => {
    // Don't run validation until map is initialized and user data is loaded
    if (
      !map ||
      !initalized ||
      !userRef.current ||
      selectedProfileIndex === undefined
    ) {
      return;
    }

    // Don't update regions if we're still in the initialization phase
    // This prevents overwriting existing regions with empty state during load
    if (!isInitialized.current) {
      return;
    }

    if (state.now.length > 0 && state.now.length < 16) {
      setErrorRef.current(false);
    } else {
      setErrorRef.current(true);
    }

    // Only update regions if we have a valid state and we're not in initialization
    // Also don't update if state is empty but we should have existing regions
    const existingRegionsCount =
      selectedProfileRef.current?.extRegions?.length ?? 0;
    const shouldUpdateRegions =
      state.now.length < 16 &&
      initalized &&
      userRef.current &&
      (state.now.length > 0 ||
        existingRegionsCount === 0 ||
        allowEmptyUpdateRef.current);

    if (shouldUpdateRegions) {
      updateRegionsRef.current(state.now);
      allowEmptyUpdateRef.current = false;
    }
  }, [map, state.now, initalized, selectedProfileIndex]);

  // We need this ref to prevent infinite loops in certain cases.
  // For example when the radius of circle is set via code (and not by user interaction)
  // the radius_changed event gets triggered again. This would cause an infinite loop.
  // This solution can be improved by comparing old vs. new values. For now we turn
  // off the "updating" when snapshot changes are applied back to the overlays.
  const overlaysShouldUpdateRef = useRef<boolean>(false);

  useTerraDrawEvents(draw, allowEmptyUpdateRef, dispatch);

  // Load saved regions into TerraDraw
  const profileIdRef = useRef(selectedProfileIndex?.toString() ?? '');
  profileIdRef.current = selectedProfileIndex?.toString() ?? '';

  useTerraDrawSnapshots(
    draw,
    selectedProfileRef.current?.extRegions,
    profileIdRef.current,
    dispatch
  );

  useEffect(() => {
    if (!draw) return;
    if (!overlaysShouldUpdateRef.current) return;

    overlaysShouldUpdateRef.current = false;
    draw.clear();

    const features = state.now
      .map((overlay) =>
        isTerraDrawFeature(overlay.geometry) ? overlay.geometry : null
      )
      .filter(
        (feature): feature is NonNullable<typeof feature> => feature !== null
      );

    if (features.length > 0) {
      draw.addFeatures(features);
    }
  }, [draw, state.now]);

  const [drawingMode, setDrawingMode] = React.useState<'select' | 'rectangle'>(
    'select'
  );

  // Sync mode state when draw instance changes or when Terra Draw internally changes mode
  React.useEffect(() => {
    if (!draw) return;

    const currentMode = draw.getMode();
    if (currentMode === 'rectangle' || currentMode === 'select') {
      setDrawingMode(currentMode);
      onDrawingModeChange?.(currentMode);
    }

    // Listen for mode changes from Terra Draw
    const handleModeChange = () => {
      const mode = draw.getMode();
      if (mode === 'rectangle' || mode === 'select') {
        setDrawingMode(mode);
        onDrawingModeChange?.(mode);
      }
    };

    // Check for mode changes periodically (Terra Draw doesn't have a mode change event)
    const interval = setInterval(handleModeChange, 100);

    return () => clearInterval(interval);
  }, [draw, onDrawingModeChange]);

  const toggleDrawingMode = () => {
    if (!draw) return;

    if (drawingMode === 'select') {
      draw.setMode('rectangle');
      setDrawingMode('rectangle');
      onDrawingModeChange?.('rectangle');
    } else {
      draw.setMode('select');
      setDrawingMode('select');
      onDrawingModeChange?.('select');
    }
  };

  return (
    <div className="drawing-history" style={{ marginTop: '4px' }}>
      <button
        onClick={toggleDrawingMode}
        disabled={!draw}
        title={drawingMode === 'select' ? 'Draw Rectangle' : 'Select/Edit'}
        style={{
          backgroundColor: drawingMode === 'rectangle' ? '#1976d2' : 'white',
          color: drawingMode === 'rectangle' ? 'white' : 'black',
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 0 24 24"
          width="16"
          fill="currentColor"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
        </svg>
      </button>
      <button
        onClick={() => {
          overlaysShouldUpdateRef.current = true;
          dispatch({ type: DrawingActionKind.UNDO });
        }}
        disabled={!map || !state.past.length}
        title="Undo"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 -960 960 960"
          width="16"
        >
          <path d="M280-200v-80h284q63 0 109.5-40T720-420q0-60-46.5-100T564-560H312l104 104-56 56-200-200 200-200 56 56-104 104h252q97 0 166.5 63T800-420q0 94-69.5 157T564-200H280Z" />
        </svg>
      </button>
      <button
        onClick={() => {
          overlaysShouldUpdateRef.current = true;
          dispatch({ type: DrawingActionKind.REDO });
        }}
        disabled={!map || !state.future.length}
        title={'Redo'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 -960 960 960"
          width="16"
        >
          <path d="M396-200q-97 0-166.5-63T160-420q0-94 69.5-157T396-640h252L544-744l56-56 200 200-200 200-56-56 104-104H396q-63 0-109.5 40T240-420q0 60 46.5 100T396-280h284v80H396Z" />
        </svg>
      </button>
      <button
        title="Clear All"
        onClick={() => {
          dispatch({ type: DrawingActionKind.CLEAR });
          if (draw) {
            draw.clear();
          }
        }}
        disabled={!map}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 0 24 24"
          width="16"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M18.3 5.71a1 1 0 0 0-1.42 0L12 10.59 7.12 5.71a1 1 0 0 0-1.42 1.42L10.59 12l-4.89 4.88a1 1 0 1 0 1.42 1.42L12 13.41l4.88 4.89a1 1 0 0 0 1.42-1.42L13.41 12l4.89-4.88a1 1 0 0 0 0-1.42z" />
        </svg>
      </button>
    </div>
  );
};
